import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from 'react'

type BankAccount = {
  bank: string
  agency: string
  account: string
  accountType: string
}

type Address = {
  zipCode: string
  state: string
  city: string
  neighborhood?: string
  street?: string
  number?: string
  complement?: string
}

type Rg = {
  number: string
  uf: string
  org: string
  date: string
}

type ReferenceContact = {
  degree: string
  name: string
  phone: string
}

type PlaceOfBirth = {
  city: string
  uf: string
}

export interface CustomerData {
  id?: string
  name: string
  cpf: string
  email?: string
  birthDate: string
  phone?: string
  ocupacaoId?: string
  renda?: number
  diaRecebimento?: string
  ocupationId?: string
  maritalStatus?: string
  spouseName?: string
  motherName?: string
  placeOfBirth?: PlaceOfBirth
  gender?: string
  referenceContact?: ReferenceContact[]
  rg?: Rg
  address?: Address
  bankAccount?: BankAccount
}

export interface ProposalData {
  propostaId: string
  tabelaJurosId?: number
  prestacao?: number
  prazo?: number
  diaRecebimento?: number
  convenioId?: number
  value?: number
}

interface CrefazContextType {
  customerData: CustomerData | null
  setCustomerData: (data: CustomerData | null) => void
  proposalData: ProposalData | null
  setProposalData: (data: ProposalData | null) => void
  transactionId: string | null
  setTransactionId: (id: string | null) => void
}

const CrefazContext = createContext<CrefazContextType | undefined>(undefined)

export function CrefazProvider({ children }: { children: ReactNode }) {
  const [customerData, setCustomerDataState] = useState<CustomerData | null>(
    () => {
      const stored = localStorage.getItem('crefaz_customer_data')
      return stored ? JSON.parse(stored) : null
    },
  )

  const [proposalData, setProposalDataState] = useState<ProposalData | null>(
    null,
  )

  const setCustomerData = useCallback((data: CustomerData | null) => {
    if (data) {
      localStorage.setItem('crefaz_customer_data', JSON.stringify(data))
    } else {
      localStorage.removeItem('crefaz_customer_data')
    }
    setCustomerDataState(data)
  }, [])

  const setProposalData = useCallback((data: ProposalData | null) => {
    setProposalDataState(data)
  }, [])

  const [transactionId, setTransactionId] = useState<string | null>(null)

  const value = useMemo(
    () => ({
      customerData,
      setCustomerData,
      proposalData,
      setProposalData,
      transactionId,
      setTransactionId,
    }),
    [
      customerData,
      setCustomerData,
      proposalData,
      setProposalData,
      transactionId,
      setTransactionId,
    ],
  )

  return (
    <CrefazContext.Provider value={value}>
      {children}
    </CrefazContext.Provider>
  )
}

export function useCrefazContext() {
  const context = useContext(CrefazContext)
  if (!context) {
    throw new Error('useCrefazContext must be used within a CrefazProvider')
  }
  return context
}
